import React, { ReactNode, useState } from 'react'
import { PamError, PamConfirmDialog } from '../components/PamStateComponents'
import { useResource } from '../resource/useResource'
import {
  CrudTable,
  CrudForm,
  CrudSecondaryButton,
  CrudDangerButton,
  CrudActionsRow,
  PageStack,
  PageTitle,
  SectionBlock,
  SectionTitle,
  TenantGateText,
} from '../components/crud'
import type { CrudColumn, CrudField } from '../components/crud'
import type { PAMEntitlement } from '../api/types'
import type {
  AdminCreateEntitlementReq,
  AdminEntitlementItem,
  AdminUpdateEntitlementReq,
} from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import { useActiveTenant } from '../layout/TenantSwitcher'

const columns: CrudColumn<PAMEntitlement>[] = [
  { key: 'resource_id', header: 'Resource' },
  {
    key: 'permissions',
    header: 'Permissions',
    render: (e) => e.permissions.join(', '),
  },
  { key: 'max_duration_seconds', header: 'Max Duration (s)' },
  { key: 'required_approvals', header: 'Approvals' },
  {
    key: 'self_approval_allowed',
    header: 'Self-Approve',
    render: (e) => (e.self_approval_allowed ? 'Yes' : 'No'),
  },
]

const fields: CrudField[] = [
  { name: 'resource_id', label: 'Resource ID', type: 'text', required: true },
  {
    name: 'candidate_subjects',
    label: 'Candidate Subjects (comma-separated)',
    type: 'text',
    required: true,
  },
  {
    name: 'permissions',
    label: 'Permissions (comma-separated, no wildcards)',
    type: 'text',
    required: true,
  },
  {
    name: 'max_duration_seconds',
    label: 'Max Duration (seconds)',
    type: 'text',
    required: true,
  },
  { name: 'required_approvals', label: 'Required Approvals', type: 'text' },
  {
    name: 'self_approval_allowed',
    label: 'Self-Approval Allowed',
    type: 'checkbox',
  },
]

const CREATE_INITIAL: Record<string, unknown> = {
  max_duration_seconds: '3600',
  required_approvals: '1',
  self_approval_allowed: false,
}

const splitCSV = (v: unknown): string[] =>
  String(v ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)

/**
 * Map the generated AdminEntitlementItem (camelCase, real client response
 * type) → PAMEntitlement (snake_case) so all existing column/form/edit logic
 * stays unchanged. Previously took an inline structural type duplicating
 * AdminEntitlementItem field-for-field; now imports the real generated type
 * (hygiene fix matching the Phase-4a Resources.tsx / Phase-4b Subjects.tsx
 * precedent, so a future codegen field-rename fails typecheck here instead
 * of silently drifting) — no behavior change, fields already matched.
 */
function toPAMEntitlement(e: AdminEntitlementItem): PAMEntitlement {
  return {
    id: e.id,
    tenant_id: e.tenantId,
    resource_id: e.resourceId,
    candidate_subjects: e.candidateSubjects ?? [],
    permissions: e.permissions ?? [],
    max_duration_seconds: e.maxDurationSeconds,
    required_approvals: e.requiredApprovals ?? 0,
    self_approval_allowed: e.selfApprovalAllowed ?? false,
    created_at: e.createdAt,
    updated_at: e.updatedAt,
  }
}

export const Entitlements = (): JSX.Element => {
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()
  const [editing, setEditing] = useState<PAMEntitlement | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDelete, setPendingDelete] = useState<PAMEntitlement | null>(
    null
  )

  const rs = useResource<PAMEntitlement>({
    list: async () => {
      const resp = await client.adminListEntitlements({ tenantId: tenant })
      return (resp.entitlements ?? []).map(toPAMEntitlement)
    },
    remove: async (id) => {
      await client.adminDeleteEntitlement({
        tenantId: tenant,
        entitlementId: id,
      })
    },
  })

  const toCreateReq = (
    v: Record<string, unknown>
  ): AdminCreateEntitlementReq => ({
    tenantId: tenant,
    resourceId: String(v.resource_id ?? ''),
    candidateSubjects: splitCSV(v.candidate_subjects),
    permissions: splitCSV(v.permissions),
    maxDurationSeconds: Number(v.max_duration_seconds) || 0,
    requiredApprovals: Number(v.required_approvals) || 0,
    selfApprovalAllowed: Boolean(v.self_approval_allowed),
  })

  const toUpdateReq = (
    entitlementId: string,
    v: Record<string, unknown>
  ): AdminUpdateEntitlementReq => ({
    tenantId: tenant,
    entitlementId,
    resourceId: String(v.resource_id ?? ''),
    candidateSubjects: splitCSV(v.candidate_subjects),
    permissions: splitCSV(v.permissions),
    maxDurationSeconds: Number(v.max_duration_seconds) || 0,
    requiredApprovals: Number(v.required_approvals) || 0,
    selfApprovalAllowed: Boolean(v.self_approval_allowed),
  })

  const handleCreate = async (v: Record<string, unknown>): Promise<void> => {
    try {
      await client.adminCreateEntitlement(toCreateReq(v))
      await rs.reload()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Create failed')
    }
  }

  const handleEdit = async (v: Record<string, unknown>): Promise<void> => {
    if (!editing) return
    try {
      await client.adminUpdateEntitlement(toUpdateReq(editing.id, v))
      await rs.reload()
      setEditing(null)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Update failed')
    }
  }

  const handleConfirmDelete = async (): Promise<void> => {
    if (!pendingDelete) return
    setActionError(null)
    await rs.remove(pendingDelete.id)
    setPendingDelete(null)
  }

  const rowActions = (e: PAMEntitlement): ReactNode => (
    <CrudActionsRow>
      <CrudSecondaryButton
        type="button"
        onClick={() => {
          setEditing(e)
          setActionError(null)
        }}
      >
        Edit
      </CrudSecondaryButton>
      <CrudDangerButton
        type="button"
        onClick={() => {
          setActionError(null)
          setPendingDelete(e)
        }}
      >
        Delete
      </CrudDangerButton>
    </CrudActionsRow>
  )

  if (tenant === 'system') {
    return (
      <PageStack>
        <TenantGateText>
          Please select a tenant to manage entitlements.
        </TenantGateText>
      </PageStack>
    )
  }

  return (
    <PageStack>
      <PageTitle>Entitlements</PageTitle>
      {(rs.error || actionError) && (
        <PamError message={rs.error ?? actionError ?? ''} />
      )}
      <SectionBlock>
        <SectionTitle>All Entitlements</SectionTitle>
        <CrudTable
          columns={columns}
          items={rs.items}
          rowActions={rowActions}
          loading={rs.loading}
          caption="Entitlements list"
          emptyMessage="No entitlements configured yet."
        />
      </SectionBlock>
      {editing && (
        <SectionBlock>
          <SectionTitle>Edit Entitlement</SectionTitle>
          <CrudForm
            key={editing.id}
            fields={fields}
            initial={{
              resource_id: editing.resource_id,
              candidate_subjects: editing.candidate_subjects.join(', '),
              permissions: editing.permissions.join(', '),
              max_duration_seconds: String(editing.max_duration_seconds),
              required_approvals: String(editing.required_approvals),
              self_approval_allowed: editing.self_approval_allowed,
            }}
            onSubmit={handleEdit}
            submitLabel="Save Changes"
          />
          <CrudSecondaryButton
            type="button"
            onClick={() => {
              setEditing(null)
              setActionError(null)
            }}
          >
            Cancel
          </CrudSecondaryButton>
        </SectionBlock>
      )}
      <SectionBlock>
        <SectionTitle>Create Entitlement</SectionTitle>
        <CrudForm
          fields={fields}
          initial={CREATE_INITIAL}
          onSubmit={handleCreate}
          submitLabel="Create Entitlement"
        />
      </SectionBlock>
      <PamConfirmDialog
        open={pendingDelete !== null}
        title="Delete entitlement"
        body={`Delete entitlement for resource "${pendingDelete?.resource_id ?? pendingDelete?.id ?? ''}"? This cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
        color="error"
      />
    </PageStack>
  )
}
